import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ChevronLeft, Share2, ChevronDown } from 'lucide-react';
import { api } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { useLang } from '../contexts/LanguageContext';
import t from '../translations';
import ShareCard from '../components/ShareCard';

/* ── Expandable section ── */
function Section({ title, icon, children, delay = 0, testId, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div
      data-testid={testId}
      className="card"
      style={{
        animationDelay: `${delay}s`,
        animation: `fadeInUp 0.35s ease ${delay}s forwards`,
        opacity: 0,
        padding: 0,
        overflow: 'hidden',
      }}
    >
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          width: '100%', display: 'flex', alignItems: 'center',
          justifyContent: 'space-between',
          padding: '16px 18px',
          background: 'none', border: 'none', cursor: 'pointer',
        }}
      >
        <h3 style={{
          fontFamily: "'Playfair Display', serif",
          fontSize: 17, fontWeight: 700, color: '#C9A84C',
          display: 'flex', alignItems: 'center', gap: 8,
        }}>
          {icon && <span style={{ fontSize: 16 }}>{icon}</span>}
          {title}
        </h3>
        <ChevronDown
          size={16}
          color="#C9A84C"
          style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.25s ease', flexShrink: 0 }}
        />
      </button>
      <div style={{
        maxHeight: open ? '800px' : '0px',
        overflow: 'hidden',
        transition: 'max-height 0.35s ease',
      }}>
        <div style={{ padding: '0 18px 18px' }}>
          <div style={{ height: 1, background: 'rgba(201,168,76,0.12)', marginBottom: 14 }} />
          {children}
        </div>
      </div>
    </div>
  );
}

export default function ReadingPage() {
  const location = useLocation();
  const navigate  = useNavigate();
  const { profile } = useAuth();
  const { lang }    = useLang();
  const tr          = t[lang];

  const [reading,   setReading]   = useState(location.state?.reading || null);
  const [loading,   setLoading]   = useState(!location.state?.reading);
  const [error,     setError]     = useState('');
  const [showShare, setShowShare] = useState(false);

  useEffect(() => {
    if (reading) return;
    (async () => {
      try {
        const data = await api.get('/api/reading/today');
        setReading(data);
      } catch (err) {
        if (err.status === 402) navigate('/paywall');
        else setError('Failed to load reading. Please try again.');
      }
      setLoading(false);
    })();
  }, []);

  if (loading) return (
    <div style={{
      minHeight: '100vh', display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center', background: '#0A0F1E',
    }}>
      <img
        src="https://static.prod-images.emergentagent.com/jobs/a18daceb-ae2c-4f94-b376-8eade8d6511f/images/fecd75b0b7001ec8bb23d55cb7dd749f25eee7eba8a99200062f858dbf83ba5e.png"
        alt="loading"
        className="rotate-mandala"
        style={{ width: 120, height: 120, opacity: 0.8, marginBottom: 20 }}
        onError={e => { e.target.style.display = 'none'; }}
      />
      <p className="shimmer-text" style={{ fontSize: 16 }}>{tr.generatingReading}</p>
    </div>
  );

  if (error) return (
    <div style={{
      minHeight: '100vh', display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center', background: '#0A0F1E', padding: 24,
    }}>
      <p style={{ color: '#C0392B', marginBottom: 20 }}>{error}</p>
      <button className="btn-primary" onClick={() => navigate('/home')}>Back to Home</button>
    </div>
  );

  const {
    jyotisNote,
    colorReading: cr,
    foodReading:  fr,
    routineReading: rr,
    remedyReading: rem,
    quoteReading:  quote,
    dashaInfo,
    moonTransit,
  } = reading;

  const today = new Date().toLocaleDateString(lang === 'hi' ? 'hi-IN' : 'en-IN',
    { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });

  return (
    <div data-testid="reading-page" style={{ background: '#0A0F1E', minHeight: '100vh', paddingBottom: 48 }}>

      {/* Header */}
      <div style={{
        padding: '52px 20px 16px',
        background: 'linear-gradient(180deg, rgba(17,24,39,1) 0%, rgba(10,15,30,0) 100%)',
      }}>
        <button
          data-testid="back-btn"
          onClick={() => navigate('/home')}
          style={{
            background: 'none', border: 'none', color: '#C9A84C',
            cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4,
            fontSize: 14, padding: 0, marginBottom: 16,
          }}
        >
          <ChevronLeft size={18} /> Home
        </button>
        <h1 style={{
          fontFamily: "'Playfair Display', serif", fontSize: 24,
          fontWeight: 700, color: '#F5F0E8', marginBottom: 4,
        }}>
          {tr.todaysReading}
        </h1>
        <p style={{ fontSize: 12, color: '#8A8580', marginBottom: 12 }}>{today}</p>

        {/* Dasha + Moon Transit Info Bar */}
        {(dashaInfo || moonTransit) && (
          <div data-testid="dasha-transit-bar" style={{
            display: 'flex', flexWrap: 'wrap', gap: 8,
          }}>
            {dashaInfo?.mahadasha && dashaInfo.mahadasha !== 'Unknown' && (
              <div style={{
                padding: '5px 12px', borderRadius: 20,
                background: 'rgba(201,168,76,0.08)', border: '1px solid rgba(201,168,76,0.2)',
                fontSize: 11, color: '#C9A84C', fontWeight: 600,
              }}>
                ✦ {dashaInfo.mahadasha}–{dashaInfo.antardasha} Dasha
              </div>
            )}
            {moonTransit?.transitHouse > 0 && (
              <div style={{
                padding: '5px 12px', borderRadius: 20,
                background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)',
                fontSize: 11, color: '#8A8580',
              }}>
                ☽ Moon in House {moonTransit.transitHouse} ({moonTransit.moonSignEn})
              </div>
            )}
          </div>
        )}
      </div>

      <div style={{ padding: '0 16px', display: 'flex', flexDirection: 'column', gap: 12 }}>

        {/* Jyoti's Note */}
        {jyotisNote && (
          <div
            data-testid="jyotis-note"
            className="card"
            style={{
              background: 'linear-gradient(135deg, rgba(201,168,76,0.08) 0%, rgba(17,24,39,0.9) 100%)',
              border: '1px solid rgba(201,168,76,0.25)',
              animation: 'fadeInUp 0.35s ease 0s forwards', opacity: 0,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
              <div style={{
                width: 32, height: 32, borderRadius: '50%',
                background: 'rgba(201,168,76,0.15)', border: '1px solid rgba(201,168,76,0.3)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 16,
              }}>
                ✦
              </div>
              <p style={{
                fontSize: 12, fontWeight: 700, color: '#C9A84C',
                letterSpacing: '0.08em', textTransform: 'uppercase',
              }}>
                {tr.jyotisNote}
              </p>
            </div>
            <p style={{ fontSize: 14, color: '#F5F0E8', lineHeight: 1.75, fontStyle: 'italic' }}>
              "{jyotisNote}"
            </p>
          </div>
        )}

        {/* Colour Section */}
        <Section title={tr.colorToday} icon="🎨" delay={0.1} testId="color-section" defaultOpen={true}>
          {cr && (
            <div>
              <div style={{
                width: '100%', height: 72, borderRadius: 10,
                background: cr.hexCode || '#C9A84C',
                marginBottom: 14,
                boxShadow: `0 4px 20px ${cr.hexCode || '#C9A84C'}44`,
              }} data-testid="color-swatch" />
              <p style={{ fontSize: 16, fontWeight: 700, color: '#F5F0E8', marginBottom: 6 }}>{cr.name}</p>
              <p style={{ fontSize: 13, color: '#8A8580', lineHeight: 1.7, marginBottom: 10 }}>{cr.reason}</p>
              {cr.clothingTip && (
                <div style={{
                  padding: '10px 12px',
                  background: 'rgba(201,168,76,0.06)', borderRadius: 8,
                  border: '1px solid rgba(201,168,76,0.12)',
                }}>
                  <span style={{ fontSize: 10, color: '#C9A84C', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                    {tr.clothingTip}
                  </span>
                  <p style={{ fontSize: 13, color: '#F5F0E8', marginTop: 3 }}>{cr.clothingTip}</p>
                </div>
              )}
            </div>
          )}
        </Section>

        {/* Food Section */}
        <Section title={tr.foodToday} icon="🍃" delay={0.15} testId="food-section">
          {fr && (
            <div>
              {fr.eat?.length > 0 && (
                <div style={{ marginBottom: 14 }}>
                  <p style={{ fontSize: 11, color: '#2D6A4F', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 8 }}>
                    ✓ {tr.eatThese}
                  </p>
                  {fr.eat.map((item, i) => (
                    <div key={i} style={{
                      paddingBottom: 8, marginBottom: 8,
                      borderBottom: i < fr.eat.length - 1 ? '1px solid rgba(255,255,255,0.04)' : 'none',
                    }}>
                      <p style={{ fontSize: 13, fontWeight: 600, color: '#F5F0E8', marginBottom: 2 }}>{item.item}</p>
                      <p style={{ fontSize: 12, color: '#8A8580' }}>{item.reason}</p>
                    </div>
                  ))}
                </div>
              )}
              {fr.avoid?.length > 0 && (
                <div>
                  <p style={{ fontSize: 11, color: '#C0392B', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 8 }}>
                    ✗ {tr.avoidThese}
                  </p>
                  {fr.avoid.map((item, i) => (
                    <div key={i} style={{
                      paddingBottom: 8, marginBottom: 8,
                      borderBottom: i < fr.avoid.length - 1 ? '1px solid rgba(255,255,255,0.04)' : 'none',
                    }}>
                      <p style={{ fontSize: 13, fontWeight: 600, color: '#F5F0E8', marginBottom: 2 }}>{item.item}</p>
                      <p style={{ fontSize: 12, color: '#8A8580' }}>{item.reason}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </Section>

        {/* Routine Section */}
        <Section title={tr.routineToday} icon="🌅" delay={0.2} testId="routine-section">
          {rr && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {[
                { key: 'morning',   label: tr.morning,   icon: '🌅' },
                { key: 'afternoon', label: tr.afternoon, icon: '☀️' },
                { key: 'evening',   label: tr.evening,   icon: '🌙' },
              ].map(slot => (
                <div key={slot.key} data-testid={`routine-${slot.key}`} style={{
                  padding: '12px 14px',
                  background: 'rgba(255,255,255,0.03)', borderRadius: 10,
                  border: '1px solid rgba(255,255,255,0.06)',
                }}>
                  <p style={{ fontSize: 11, color: '#C9A84C', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 4 }}>
                    {slot.label}
                  </p>
                  <p style={{ fontSize: 13, color: '#F5F0E8', lineHeight: 1.7 }}>{rr[slot.key]}</p>
                </div>
              ))}
            </div>
          )}
        </Section>

        {/* Today's Remedy */}
        {rem && (
          <div
            data-testid="remedy-section"
            className="card"
            style={{
              background: 'rgba(45,106,79,0.06)', border: '1px solid rgba(45,106,79,0.2)',
              animation: 'fadeInUp 0.35s ease 0.25s forwards', opacity: 0,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
              <span style={{ fontSize: 18 }}>🪔</span>
              <h3 style={{ fontFamily: "'Playfair Display', serif", fontSize: 17, fontWeight: 700, color: '#2D6A4F' }}>
                {tr.todaysRemedy}
              </h3>
            </div>
            <p style={{ fontSize: 14, fontWeight: 700, color: '#F5F0E8', marginBottom: 6 }}>{rem.title}</p>
            <p style={{ fontSize: 13, color: '#8A8580', lineHeight: 1.7, marginBottom: 8 }}>{rem.description}</p>
            {rem.timing && (
              <div style={{
                padding: '8px 12px', background: 'rgba(45,106,79,0.08)',
                borderRadius: 8, border: '1px solid rgba(45,106,79,0.15)',
              }}>
                <span style={{ fontSize: 10, color: '#2D6A4F', textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 700 }}>
                  {tr.remedyTiming}
                </span>
                <p style={{ fontSize: 12, color: '#F5F0E8', marginTop: 2 }}>{rem.timing}</p>
              </div>
            )}
          </div>
        )}

        {/* Cosmic Wisdom Quote */}
        {quote && (
          <div
            data-testid="quote-section"
            style={{
              padding: '20px 18px',
              background: 'rgba(201,168,76,0.04)', borderRadius: 16,
              border: '1px solid rgba(201,168,76,0.12)',
              animation: 'fadeInUp 0.35s ease 0.3s forwards', opacity: 0,
              textAlign: 'center',
            }}
          >
            <p style={{ fontSize: 10, color: '#C9A84C', textTransform: 'uppercase', letterSpacing: '0.12em', fontWeight: 700, marginBottom: 12 }}>
              ✦ {tr.cosmicWisdom} ✦
            </p>
            {typeof quote === 'object' ? (
              <>
                {(quote.shloka || quote.sanskrit) && (
                  <p style={{ fontSize: 14, color: '#F5F0E8', lineHeight: 1.9, fontStyle: 'italic', marginBottom: 6, fontFamily: "'Noto Sans Devanagari', serif" }}>
                    {quote.shloka || quote.sanskrit}
                  </p>
                )}
                {quote.transliteration && (
                  <p style={{ fontSize: 12, color: '#C9A84C', lineHeight: 1.7, fontStyle: 'italic', marginBottom: 6 }}>
                    {quote.transliteration}
                  </p>
                )}
                {quote.meaning && (
                  <p style={{ fontSize: 13, color: '#8A8580', lineHeight: 1.6 }}>
                    {quote.meaning}
                  </p>
                )}
              </>
            ) : (
              <p style={{ fontSize: 13, color: '#F5F0E8', lineHeight: 1.8, fontStyle: 'italic' }}>
                {quote}
              </p>
            )}
          </div>
        )}

        {/* Share button */}
        <div style={{ textAlign: 'center', padding: '4px 0 8px' }}>
          <button
            data-testid="share-reading-btn"
            onClick={() => setShowShare(true)}
            className="btn-secondary"
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, maxWidth: 260, margin: '0 auto' }}
          >
            <Share2 size={16} />
            {tr.shareReading}
          </button>
        </div>

        {/* Disclaimer footer */}
        <div data-testid="reading-disclaimer" style={{
          padding: '12px 16px',
          background: 'rgba(255,255,255,0.02)', borderRadius: 10,
          border: '1px solid rgba(255,255,255,0.05)',
        }}>
          <p style={{ fontSize: 10, color: 'rgba(138,133,128,0.5)', lineHeight: 1.6, textAlign: 'center' }}>
            {tr.readingDisclaimer}
          </p>
        </div>

        <p style={{ fontSize: 11, color: 'rgba(138,133,128,0.4)', textAlign: 'center', paddingBottom: 4 }}>
          ✦ {tr.appName} ✦
        </p>

      </div>

      {showShare && (
        <ShareCard reading={reading} profile={profile} onClose={() => setShowShare(false)} />
      )}
    </div>
  );
}
